import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_BASE_URL } from '../config/api';

export interface ApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
  traceId: string | null;
}

export interface ApiError {
  message: string;
  status: number;
  data?: unknown;
  /** İstek kullanıcı tarafından iptal edildiyse true. İptal bir hata değildir. */
  canceled?: boolean;
}

/** Uçuştaki isteğe iliştirilen dahili kimlik; iptal kaydından düşmek için kullanılır. */
interface TrackedRequestConfig {
  __requestId?: number;
}

class HttpClient {
  private axiosInstance: AxiosInstance;
  private baseURL: string;
  private loadingCallbacks: {
    showLoading?: (message?: string) => void;
    hideLoading?: () => void;
  } = {};
  /** Uçuştaki istekler; kullanıcı iptal edince bunların controller'ı abort edilir. */
  private pendingRequests = new Map<number, { controller: AbortController; method: string }>();
  private requestCounter = 0;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      // Backend'in Command Timeout'u 60 sn. Buradaki tavan bilerek onun üstünde:
      // böylece yarışı backend kazanır ve kullanıcıya sunucunun anlamlı Türkçe
      // hata cevabı ulaşır, client'ın ham iptali değil.
      // Uzun tavan kullanıcıyı mahkûm etmiyor; yükleme panelinde İptal butonu var.
      timeout: 65000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add authentication token if available
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // İsteği iptal kaydına al. Çağıran kendi signal'ını verdiyse ona dokunma.
        if (!config.signal) {
          const controller = new AbortController();
          const requestId = ++this.requestCounter;
          config.signal = controller.signal;
          (config as typeof config & TrackedRequestConfig).__requestId = requestId;
          this.pendingRequests.set(requestId, {
            controller,
            method: config.method?.toUpperCase() || 'GET',
          });
        }

        // Show loading for API requests (except auth endpoints)
        if (!config.url?.includes('/Auth/')) {
          const method = config.method?.toUpperCase() || 'REQUEST';
          const endpoint = config.url || 'Unknown';
          this.showLoading(`${method} ${endpoint}`);
        }

        // Log request in development - removed for production

        return config;
      },
      (error) => {
        this.hideLoading();
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Hide loading on successful response
        this.forgetRequest(response.config);
        this.hideLoading();

        // Log response in development - removed for production

        return response;
      },
      (error: AxiosError) => {
        // Hide loading on error
        this.forgetRequest(error.config);
        this.hideLoading();

        // Log error in development - removed for production

        // Handle common error cases
        if (error.response?.status === 401) {
          // Unauthorized - clear token and redirect to login
          this.clearAuthToken();
          // You can add redirect logic here if needed
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  private clearAuthToken(): void {
    localStorage.removeItem('authToken');
  }

  private handleError(error: AxiosError): ApiError {
    // İptal bir hata değildir; status koduna göre mesaj üretme aşamasına hiç girmez.
    if (axios.isCancel(error)) {
      return {
        message: 'İstek iptal edildi',
        status: 0,
        canceled: true,
      };
    }

    // Client tarafı zaman aşımı: sunucudan cevap hiç gelmedi. Kullanıcıya ham
    // "timeout of 65000ms exceeded" yerine ne olduğunu anlatan bir mesaj verilir.
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return {
        message: 'Sistem şu anda yoğun, işlem tamamlanamadı. Lütfen birkaç dakika sonra tekrar deneyin.',
        status: 0,
      };
    }

    const apiError: ApiError = {
      message: error.message || 'An unexpected error occurred',
      status: error.response?.status || 0,
      data: error.response?.data,
    };

    // Backend zaten Türkçe ve duruma özel bir mesaj döndüyse onu kullan.
    // Aşağıdaki switch yalnızca sunucu mesaj vermediğinde devreye girer —
    // aksi halde "İstek zaman aşımına uğradı" gibi anlamlı mesajlar,
    // sabit İngilizce metinlerin altında kalıyordu.
    const backendMessage = (error.response?.data as { message?: string } | undefined)?.message;
    if (backendMessage) {
      apiError.message = backendMessage;
      return apiError;
    }

    // Handle specific HTTP status codes
    switch (error.response?.status) {
      case 400:
        apiError.message = 'Bad Request - Please check your input';
        break;
      case 401:
        apiError.message = 'Unauthorized - Please login again';
        break;
      case 403:
        apiError.message = 'Forbidden - You do not have permission';
        break;
      case 404:
        apiError.message = 'Not Found - The requested resource was not found';
        break;
      case 408:
        apiError.message = 'Sistem şu anda yoğun, sorgu tamamlanamadı. Lütfen birkaç dakika sonra tekrar deneyin.';
        break;
      case 422:
        apiError.message = 'Validation Error - Please check your input';
        break;
      case 500:
        apiError.message = 'Internal Server Error - Please try again later';
        break;
      case 502:
        apiError.message = 'Bad Gateway - Server is temporarily unavailable';
        break;
      case 503:
        apiError.message = 'Service Unavailable - Please try again later';
        break;
    }

    return apiError;
  }

  // HTTP Methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.get<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.post<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.put<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.patch<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.delete<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Utility methods
  setAuthToken(token: string): void {
    localStorage.setItem('authToken', token);
  }

  removeAuthToken(): void {
    this.clearAuthToken();
  }

  updateBaseURL(newBaseURL: string): void {
    this.baseURL = newBaseURL;
    this.axiosInstance.defaults.baseURL = newBaseURL;
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  // Method to get the raw axios instance if needed
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  // İptal yönetimi

  /** Tamamlanan isteği iptal kaydından düşürür. */
  private forgetRequest(config?: unknown): void {
    const requestId = (config as TrackedRequestConfig | undefined)?.__requestId;
    if (requestId !== undefined) {
      this.pendingRequests.delete(requestId);
    }
  }

  /**
   * Uçuştaki okuma (GET) isteklerini iptal eder ve iptal edilen sayısını döner.
   *
   * POST/PUT/PATCH/DELETE bilerek kapsam dışı: sunucu isteği çoktan işlemiş
   * olabilir. Yarıda kesmek kullanıcıya "kaydedilmedi" izlenimi verir ama kayıt
   * commit edilmiş olabilir — bu, çözdüğümüzden daha kötü bir tutarsızlık olur.
   */
  cancelPendingReads(): number {
    let canceledCount = 0;

    this.pendingRequests.forEach(({ controller, method }, requestId) => {
      if (method === 'GET') {
        controller.abort();
        this.pendingRequests.delete(requestId);
        canceledCount++;
      }
    });

    return canceledCount;
  }

  /** Uçuşta iptal edilebilir (GET) istek var mı? */
  hasPendingReads(): boolean {
    let found = false;
    this.pendingRequests.forEach(({ method }) => {
      if (method === 'GET') {
        found = true;
      }
    });
    return found;
  }

  // Loading management methods
  setLoadingCallbacks(callbacks: {
    showLoading?: (message?: string) => void;
    hideLoading?: () => void;
  }): void {
    this.loadingCallbacks = callbacks;
  }

  private showLoading(message?: string): void {
    if (this.loadingCallbacks.showLoading) {
      this.loadingCallbacks.showLoading(message);
    }
  }

  private hideLoading(): void {
    if (this.loadingCallbacks.hideLoading) {
      this.loadingCallbacks.hideLoading();
    }
  }
}

// Create and export a singleton instance
export const httpClient = new HttpClient();

// Export the class for creating custom instances if needed
export default HttpClient;

// Re-export for generated API compatibility
export { httpClient as apiClient };